export type MasterDataGraphQLResult = {
  error?: Error;
  errors?: readonly { message: string }[];
};

export type MasterDataPaginatedResponse<TItem> = {
  items: TItem[];
  total: number;
  page: number;
  limit: number;
};

/** Shared GraphQL error handling for Master Data list repositories. */
export function throwIfMasterDataGraphQLError(
  result: MasterDataGraphQLResult,
): void {
  if (result.error instanceof Error && result.error.message.trim()) {
    throw result.error;
  }
  const messages = result.errors
    ?.map((entry) => entry.message)
    .filter(Boolean)
    .join(", ");
  if (messages) {
    throw new Error(messages);
  }
}

/** Shared GraphQL error handling for Master Data mutation repositories. */
export function throwIfMasterDataMutationError(error: unknown): void {
  throwIfMasterDataGraphQLError({
    error: error instanceof Error ? error : undefined,
    errors:
      error &&
      typeof error === "object" &&
      "errors" in error &&
      Array.isArray((error as { errors: unknown }).errors)
        ? ((error as { errors: readonly { message: string }[] }).errors)
        : undefined,
  });
}

/** Require mutation payload or throw the GraphQL / fallback error message. */
export function requireMasterDataMutationData<T>(
  data: T | null | undefined,
  error: unknown,
  fallbackMessage: string,
): T {
  if (data != null) {
    return data;
  }
  const graphQlMessage = error instanceof Error ? error.message : null;
  throw new Error(graphQlMessage?.trim() || fallbackMessage);
}

/** Map a paginated GraphQL payload into the repository list result shape. */
export function toMasterDataListResult<TItem, TMapped>(
  response: MasterDataPaginatedResponse<TItem>,
  mapItem: (item: TItem) => TMapped,
  fallbackPage: number,
  fallbackLimit: number,
): {
  items: TMapped[];
  total: number;
  page: number;
  limit: number;
} {
  return {
    items: (response.items ?? []).map(mapItem),
    total: response.total ?? 0,
    page: response.page ?? fallbackPage,
    limit: response.limit ?? fallbackLimit,
  };
}
